/**
 * ChannelRankingTable · Module B - 渠道表现排名表
 *
 * 接口契约：specs/多游戏投放对比页/api/sum.md
 * 需求 §4.4.5：
 *   - 行 = 游戏（icon + 名称）
 *   - 列 = 5 个指标：
 *     1) Ad Creatives                  (creative_count_stat)
 *     2) Active Ads Channel Share      (creative_count_stat_by_channel_percent) ← 仅当渠道筛选有值时显示
 *     3) Active Ads Competitor Share   (creative_count_stat_by_competitor_percent) + 进度条
 *     4) Active Ads Score              (creative_score_stat)
 *     5) Active Ads Impressions        (impression_stat)
 *
 * - **前端分页**（默认 10/页）
 * - **前端排序**（点击列头切升降序）
 * - Hover 列头显示指标说明（Channel Share / Competitor Share）
 * - **数值 cell**：
 *     · 数值 + PercentageItem（环比箭头，已在 hook 用 DBO_V.qoq 预算）
 *     · 外层 RingRatioBubble，hover 显示「当前周期 VS 上一周期」对比气泡
 * - 字段名直接采用 sum 接口 metric 全名
 *
 * ⚠️ 数据契约：row 仅 game_id；游戏元信息走 gameMap（cond）
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import BubbleCommon, { TipsMapType } from '@op/components/BubbleCommon';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { SortBy } from 'tea-component/lib/table/addons';
import useIntls from '@opm/hooks/useIntls';
import { IChannelRankingItem } from '../../types/channel';
import { TGameMap } from '../../hooks/useGameMap';
import { IPageInfoState, ISortByState } from '../../hooks/useChannelCompare';
import GameCell from '../KpiOverview/GameCell';
import './index.scss';
import {
  adCreativesComparisonChannelShareTipsMap,
  adCreativesComparisonCompetitorShareTipsMap,
} from '@opm/components/BubbleCommon';
import { METRIC_METAS, getMetricLabel } from '../../constants/metrics';

/** 表格列头 i18n key 常量 */
export const RANKING_LABEL_GAME = 'Game';
export const RANKING_LABEL_RELEASE_DATE = 'Release Date';

export interface IChannelRankingTableProps {
  list: IChannelRankingItem[];
  total: number;
  pageInfo: IPageInfoState;
  setPageInfo: (info: IPageInfoState) => void;
  sortBy: ISortByState;
  setSortBy: (s: ISortByState) => void;
  gameMap: TGameMap;
  loading?: boolean;
  /**
   * 当前选中的渠道列表（与 ChannelSelect 协议一致：[] = 全选/未筛选）
   * 用于决定是否展示 Channel Share 列：
   * - 未筛选渠道（全部）→ 隐藏（百分比恒为 100%，无意义）
   * - 选中具体渠道       → 展示
   */
  channelIds?: string[];
  /** 当前周期时间窗口（RingRatioBubble 用；来自 sum.link_time） */
  newTime?: [string, string];
  /** 上一周期时间窗口（RingRatioBubble 用） */
  oldTime?: [string, string];
  emptyTitleText?: string;
  /** 日期粒度模式（来自 getDayMode，映射后为 'daily' | 'daily_range' | 'weekly' | 'monthly'），用于按粒度切换列头文案 */
  dayMode?: string;
  gotoAdDeliveryOverview?:(id: string) => void;
}

const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 56;
const MIN_HEIGHT = 280;

/** 整数缩写：> 0 走 DBO_V.reviews，否则 '-' */
const formatReviews = (value: number): string =>
  value > 0 ? String(DBO_V.reviews(value)) : '-';

/** 百分比 (0~1 → "xx.x%") */
const formatPercent = (value: number): string =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '-';

export default function ChannelRankingTable({
  list,
  total,
  pageInfo,
  setPageInfo,
  sortBy,
  setSortBy,
  gameMap,
  loading,
  channelIds,
  newTime,
  oldTime,
  emptyTitleText,
  dayMode,
  gotoAdDeliveryOverview
}: IChannelRankingTableProps) {
  const { intl } = useIntls();
  const hasChannelFilter = !!channelIds && channelIds.length > 0;

  const labels = useMemo(
    () => ({
      game: intl(RANKING_LABEL_GAME),
      adCreatives: intl(
        getMetricLabel(METRIC_METAS.creative_count_stat, dayMode as any),
      ),
      channelShare: intl(
        getMetricLabel(
          METRIC_METAS.creative_count_stat_by_channel_percent,
          dayMode as any,
        ),
      ),
      competitorShare: intl(
        getMetricLabel(
          METRIC_METAS.creative_count_stat_by_competitor_percent,
          dayMode as any,
        ),
      ),
      score: intl(
        getMetricLabel(METRIC_METAS.creative_score_stat, dayMode as any),
      ),
      impressions: intl(
        getMetricLabel(METRIC_METAS.impression_stat, dayMode as any),
      ),
      releaseDate: intl(RANKING_LABEL_RELEASE_DATE),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, dayMode],
  );

  /** 包装 header 为带 BubbleCommon 的 hover 提示（用于带 tip 的列） */
  const headerWithTip = (text: string, tipsMap: TipsMapType) => (
    <BubbleCommon tipsMap={tipsMap} placement="top" showIcon={true}>
      <span>{text}</span>
    </BubbleCommon>
  );

  /** 默认时间窗口（接口未返回 link_time 时退化为 ['', '']，RingRatioBubble 内部会兜底） */
  const safeNewTime = useMemo<[string, string]>(
    () => newTime ?? ['', ''],
    [newTime],
  );
  const safeOldTime = useMemo<[string, string]>(
    () => oldTime ?? ['', ''],
    [oldTime],
  );

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'game',
        header: (
          <div className="dbom-channel-ranking__header">
            #<span>{labels.game}</span>
          </div>
        ),
        width: 240,
        fixed: 'left' as const,
        render: (row: IChannelRankingItem, _rowKey: string, index: number) => {
          const game = gameMap[row.game_id];
          const ranking = index + 1 + pageInfo.page_size * (pageInfo.page - 1);
          return (
            <GameCell
              gameName={game?.game_name || row.game_id}
              cover={game?.cover}
              releaseDate={game?.release_date}
              releaseDateLabel={labels.releaseDate}
              ranking={ranking}
              onClick={() => gotoAdDeliveryOverview?.(row.game_id)}
            />
          );
        },
      },
      {
        key: 'creative_count_stat',
        header: labels.adCreatives,
        align: 'right' as const,
        width: 180,
        render: (row: IChannelRankingItem) => (
          <RingRatioBubble
            newTime={safeNewTime}
            oldTime={safeOldTime}
            newValue={row.creative_count_stat}
            oldValue={row.link_creative_count_stat}
            formateValue={(v) => formatReviews(Number(v))}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          >
            <span className="dbom-channel-ranking__value-cell">
              <span>{formatReviews(row.creative_count_stat)}</span>
              <PercentageItem percentage={row.qoq_creative_count_stat} />
            </span>
          </RingRatioBubble>
        ),
      },
    ];

    // Channel Share 仅在筛选了具体渠道时展示
    if (hasChannelFilter) {
      cols.push({
        key: 'creative_count_stat_by_channel_percent',
        header: headerWithTip(
          labels.channelShare,
          adCreativesComparisonChannelShareTipsMap,
        ),
        align: 'right' as const,
        width: 220,
        render: (row: IChannelRankingItem) => (
          <RingRatioBubble
            newTime={safeNewTime}
            oldTime={safeOldTime}
            newValue={row.creative_count_stat_by_channel_percent}
            oldValue={row.link_creative_count_stat_by_channel_percent}
            formateValue={(v) => formatPercent(Number(v))}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          >
            <span className="dbom-channel-ranking__value-cell">
              <span>
                {formatPercent(row.creative_count_stat_by_channel_percent)}
              </span>
              <PercentageItem
                percentage={row.qoq_creative_count_stat_by_channel_percent}
              />
            </span>
          </RingRatioBubble>
        ),
      });
    }

    cols.push(
      {
        key: 'creative_count_stat_by_competitor_percent',
        header: headerWithTip(
          labels.competitorShare,
          adCreativesComparisonCompetitorShareTipsMap,
        ),
        align: 'right' as const,
        width: 220,
        render: (row: IChannelRankingItem) => (
          <RingRatioBubble
            newTime={safeNewTime}
            oldTime={safeOldTime}
            newValue={row.creative_count_stat_by_competitor_percent}
            oldValue={row.link_creative_count_stat_by_competitor_percent}
            formateValue={(v) => formatPercent(Number(v))}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          >
            <span className="dbom-channel-ranking__value-cell">
              <span>
                {formatPercent(row.creative_count_stat_by_competitor_percent)}
              </span>
              <PercentageItem
                percentage={row.qoq_creative_count_stat_by_competitor_percent}
              />
            </span>
          </RingRatioBubble>
        ),
      },
      {
        key: 'creative_score_stat',
        header: labels.score,
        align: 'right' as const,
        width: 180,
        render: (row: IChannelRankingItem) => (
          <RingRatioBubble
            newTime={safeNewTime}
            oldTime={safeOldTime}
            newValue={row.creative_score_stat}
            oldValue={row.link_creative_score_stat}
            formateValue={(v) => formatReviews(Number(v))}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          >
            <span className="dbom-channel-ranking__value-cell">
              <span>{formatReviews(row.creative_score_stat)}</span>
              <PercentageItem percentage={row.qoq_creative_score_stat} />
            </span>
          </RingRatioBubble>
        ),
      },
      {
        key: 'impression_stat',
        header: labels.impressions,
        align: 'right' as const,
        width: 200,
        render: (row: IChannelRankingItem) => (
          <RingRatioBubble
            newTime={safeNewTime}
            oldTime={safeOldTime}
            newValue={row.impression_stat}
            oldValue={row.link_impression_stat}
            formateValue={(v) => formatReviews(Number(v))}
            formateDate={(date: string) => DBO_TM.sFormat(date)}
          >
            <span className="dbom-channel-ranking__value-cell">
              <span>{formatReviews(row.impression_stat)}</span>
              <PercentageItem percentage={row.qoq_impression_stat} />
            </span>
          </RingRatioBubble>
        ),
      },
    );

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, gameMap, hasChannelFilter, safeNewTime, safeOldTime, pageInfo]);

  /** 可排序的列：所有数值列 */
  const sortColumns = useMemo(() => {
    const cols = [
      'creative_count_stat',
      'creative_count_stat_by_competitor_percent',
      'creative_score_stat',
      'impression_stat',
    ];
    if (hasChannelFilter) {
      cols.splice(1, 0, 'creative_count_stat_by_channel_percent');
    }
    return cols;
  }, [hasChannelFilter]);

  /** TableCommonHook sortInfo 适配 */
  const sortValue = useMemo<SortBy<any>>(
    () => ({
      by: sortBy.by ?? '',
      order: sortBy.order ?? 'desc',
    }),
    [sortBy],
  );
  const handleSortChange = (s: SortBy<any>) => {
    setSortBy({ by: s.by || undefined, order: s.order });
  };

  const tableHeight = useMemo(
    () => Math.max(MIN_HEIGHT, HEADER_HEIGHT + list.length * ROW_HEIGHT),
    [list.length],
  );

  const recordKey = useMemo(() => (r: IChannelRankingItem) => r.game_id, []);

  const empty = useMemo(() => {
    return (
      !list?.length ||
      list.every(
        (item) =>
          !item.creative_count_stat &&
          !item.creative_score_stat &&
          !item.impression_stat,
      )
    );
  }, [list]);

  return (
    <TableCommonHook
      className="dbom-channel-ranking-table"
      records={empty ? [] : list}
      columns={columns}
      recordKey={recordKey}
      total={empty ? 0 : total}
      pageInfo={pageInfo}
      setPageInfo={setPageInfo}
      defaultPageInfo={{ page: 1, page_size: 10 }}
      pageSizeOptions={[10, 20, 50]}
      sortColumns={sortColumns}
      sortType="server"
      sortInfo={[sortValue, handleSortChange]}
      changeSort={handleSortChange}
      loading={loading}
      height={tableHeight}
      emptyProps={{ emptyTitleText, hasDesc: false }}
      bordered={'all'}
    />
  );
}
